// Очередь заявок продавцов на участие в акциях (П3, fd-promo-32): акции
// площадки и чужие публичные коллаборации. Одобрение переносит товары заявки
// в акцию (в коллаборации магазин становится участником), отказ — только с
// причиной; о решении магазин узнаёт уведомлением.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, type AdminPromoApplication, type PromoApplicationStatus } from "@/api";
import { dateTimeRu, num, rub } from "@/components/promo-kit";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { cn } from "@/lib/utils";
import { useCanManagePromos } from "./promos";
const STATUS_LABEL: Record<PromoApplicationStatus, string> = {
  pending: "На рассмотрении",
  approved: "Одобрена",
  rejected: "Отклонена",
  withdrawn: "Отозвана продавцом"
};
const STATUS_CLASS: Record<PromoApplicationStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  withdrawn: "bg-muted text-muted-foreground"
};
const FILTERS: {
  key: string;
  label: string;
}[] = [{
  key: "pending",
  label: "Ждут решения"
}, {
  key: "approved",
  label: "Одобренные"
}, {
  key: "rejected",
  label: "Отклонённые"
}, {
  key: "all",
  label: "Все"
}];
const TARGET_LABEL = {
  platform_campaign: "Акция площадки",
  collaboration: "Коллаборация"
} as const;
export function PromoApplicationsPage() {
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "pending";
  const targetId = params.get("targetId") ?? "";
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ["promo-applications", status, targetId],
    queryFn: () => api.promoCampaigns.applications({
      status,
      targetId: targetId || undefined
    })
  });
  const [rejecting, setRejecting] = useState<AdminPromoApplication | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["promo-applications"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["platform-promo-campaigns"]
    });
  };
  const approve = async (application: AdminPromoApplication) => {
    setBusyId(application.id);
    setActionError(null);
    try {
      await api.promoCampaigns.review(application.id, {
        status: "approved"
      });
      await refresh();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };
  const setFilter = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === "pending") next.delete("status");else next.set("status", key);
    setParams(next, {
      replace: true
    });
  };
  const list = data ?? [];
  const shownError = actionError ?? (error ? (error as Error).message : null);
  return <div className="flex flex-col gap-4" data-testid="promo-applications">
			<div>
				<h1 className="text-xl font-semibold">Заявки на участие в акциях</h1>
				<p className="text-sm text-muted-foreground">
					Продавцы просятся в акции площадки и в коллаборации других магазинов. Одобрение добавляет их товары в акцию, отказ
					отправляется продавцу с причиной.{" "}
					<Link to="/platform-promo-campaigns" className="underline">
						Акции площадки
					</Link>
				</p>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				{FILTERS.map(f => <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={cn("rounded-full border px-3 py-1 text-sm", status === f.key ? "border-primary bg-primary/5 font-medium" : "text-muted-foreground hover:bg-muted")}>
						{f.label}
					</button>)}
				{targetId && <button type="button" onClick={() => {
        const next_0 = new URLSearchParams(params);
        next_0.delete("targetId");
        setParams(next_0, {
          replace: true
        });
      }} className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
						Одна акция
						<X className="size-3.5" />
					</button>}
			</div>

			{shownError && <p className="text-sm text-destructive">{shownError}</p>}
			{isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
			{!isLoading && list.length === 0 && <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">Заявок нет</div>}

			<div className="flex flex-col gap-3">
				{list.map(a => <article key={a.id} className="flex flex-col gap-3 rounded-lg border p-4" data-testid={`application-${a.id}`}>
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="min-w-0">
								<div className="font-medium">{a.shopName}</div>
								<div className="text-sm text-muted-foreground">
									{TARGET_LABEL[a.targetKind]} «{a.targetTitle}»
									{a.targetStartsAt && ` · ${dateTimeRu(a.targetStartsAt)} - ${dateTimeRu(a.targetEndsAt)}`}
								</div>
								<div className="text-xs text-muted-foreground">Подана {dateTimeRu(a.createdAt)}</div>
							</div>
							<span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_CLASS[a.status])}>
								{STATUS_LABEL[a.status]}
							</span>
						</div>

						<div>
							<div className="mb-1.5 text-sm">Товары: {num(a.products.length)}</div>
							<div className="flex flex-wrap gap-2">
								{a.products.map(p => <Link key={p.id} to={`/products/${encodeURIComponent(p.id)}`} className="flex w-[220px] items-center gap-2 rounded-md border p-1.5 text-xs hover:bg-muted">
										{p.photo ? <img src={p.photo} alt="" className="size-9 shrink-0 rounded object-cover" /> : <div className="size-9 shrink-0 rounded bg-muted" />}
										<span className="min-w-0">
											<span className="block truncate">{p.name}</span>
											<span className="text-muted-foreground">{rub(p.price)}</span>
										</span>
									</Link>)}
							</div>
						</div>

						{a.comment && <p className="rounded-md bg-muted/60 px-3 py-2 text-sm">{a.comment}</p>}
						{a.status === "rejected" && a.reason && <p className="text-sm text-destructive">Причина отказа: {a.reason}</p>}

						{canManage && a.status === "pending" && <div className="flex flex-wrap gap-2">
								<Button onClick={() => void approve(a)} disabled={busyId === a.id} data-testid={`approve-${a.id}`}>
									<Check className="mr-2 size-4" />
									Одобрить
								</Button>
								<Button variant="outline" onClick={() => setRejecting(a)} disabled={busyId === a.id} data-testid={`reject-${a.id}`}>
									<X className="mr-2 size-4" />
									Отклонить
								</Button>
							</div>}
					</article>)}
			</div>

			<RejectDialog application={rejecting} onClose={() => setRejecting(null)} onDone={async () => {
      setRejecting(null);
      await refresh();
    }} />
		</div>;
}
function RejectDialog({
  application,
  onClose,
  onDone
}: {
  application: AdminPromoApplication | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!application) return;
    if (!reason.trim()) return setError("Укажите причину отказа - её увидит продавец");
    setBusy(true);
    setError(null);
    try {
      await api.promoCampaigns.review(application.id, {
        status: "rejected",
        reason: reason.trim()
      });
      setReason("");
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={Boolean(application)} onOpenChange={next => {
    if (!next) {
      setReason("");
      setError(null);
      onClose();
    }
  }}>
			<DialogContent className="sm:max-w-lg" data-testid="reject-dialog">
				<DialogHeader>
					<DialogTitle>Отклонить заявку</DialogTitle>
					<DialogDescription>
						{application?.shopName} · «{application?.targetTitle}». Причина уйдёт продавцу в уведомлении.
					</DialogDescription>
				</DialogHeader>
				<textarea className="min-h-[96px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={reason} maxLength={1000} placeholder="Например: скидка меньше 15 %, условия акции не выполнены" onChange={e_0 => setReason(e_0.target.value)} data-testid="reject-reason" />
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={busy}>
						Отмена
					</Button>
					<Button variant="destructive" onClick={() => void submit()} disabled={busy} data-testid="reject-submit">
						Отклонить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}